import React from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import BlockIcon from '@material-ui/icons/Block'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import EventIcon from '@material-ui/icons/Event'
import EventBusyOutlinedIcon from '@material-ui/icons/EventBusyOutlined'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import BlockTimeDestroyDialog from '../../blocked-times/block-time-manager/block-time-destroy-dialog'
import { formatTime12 } from './utils'

const GOOGLE_SYNCED_BLOCK_ID = '0'

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#FFEBEE',
    border: '1px solid #FFCDD2',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1, 1.25),
      gap: theme.spacing(1),
    },
  },
  cardSynced: {
    backgroundColor: '#ECEFF1',
    border: '1px dashed #90A4AE',
  },
  chip: {
    backgroundColor: '#FFCDD2',
    color: '#B71C1C',
    fontWeight: 500,
    height: 26,
    borderRadius: 13,
  },
  chipIcon: {
    color: '#B71C1C',
    marginLeft: theme.spacing(0.5),
  },
  chipSynced: {
    backgroundColor: '#CFD8DC',
    color: '#37474F',
  },
  chipIconSynced: {
    color: '#37474F',
    marginLeft: theme.spacing(0.5),
  },
  syncedIndicator: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  textCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  date: {
    fontWeight: 500,
    lineHeight: 1.3,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(5, 2),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(4, 2),
    },
  },
  emptyIcon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 117, 202, 0.08)',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  emptyCaption: {
    color: 'rgba(0, 0, 0, 0.64)',
    maxWidth: 480,
    marginBottom: theme.spacing(2),
  },
}))

const formatBlockDate = (startISO, endISO, allDay) => {
  const start = DateTime.fromISO(startISO)
  const end = DateTime.fromISO(endISO)
  if (allDay) {
    if (start.hasSame(end, 'day')) {
      return start.toFormat('EEE, MMM d')
    }
    return `${start.toFormat('EEE, MMM d')} – ${end.toFormat('EEE, MMM d')}`
  }
  return start.toFormat('EEE, MMM d')
}

const formatBlockTime = (startISO, endISO, allDay) => {
  if (allDay) return 'All day'
  return `${formatTime12(startISO)}–${formatTime12(endISO)}`
}

const BlocksList = ({ blocks, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!blocks || blocks.length === 0) {
    return (
      <Box className={classes.empty}>
        <Box className={classes.emptyIcon}>
          <EventBusyOutlinedIcon style={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h6" className={classes.emptyTitle}>
          No Blocked Times
        </Typography>
        <Typography variant="body2" className={classes.emptyCaption}>
          Block off vacations, time off, or any other commitment so students
          can't book sessions with you during those windows.
        </Typography>
        {emptyAction}
      </Box>
    )
  }

  const sorted = [...blocks].sort(
    (a, b) =>
      DateTime.fromISO(a.startTime).toMillis() -
      DateTime.fromISO(b.startTime).toMillis(),
  )

  return (
    <Box>
      {sorted.map(block => {
        const timeText = formatBlockTime(
          block.startTime,
          block.endTime,
          block.allDay,
        )
        const note = block.blockNote
        const isSynced = block.id === GOOGLE_SYNCED_BLOCK_ID
        const cardKey = isSynced
          ? `synced-${block.startTime}-${block.endTime}`
          : block.id
        return (
          <Card
            key={cardKey}
            className={`${classes.card} ${isSynced ? classes.cardSynced : ''}`}
            variant="outlined"
          >
            <Chip
              size="small"
              label={isSynced ? 'Synced' : 'Blocked'}
              className={`${classes.chip} ${
                isSynced ? classes.chipSynced : ''
              }`}
              icon={
                isSynced ? (
                  <EventIcon
                    className={classes.chipIconSynced}
                    style={{ fontSize: 14 }}
                  />
                ) : (
                  <BlockIcon
                    className={classes.chipIcon}
                    style={{ fontSize: 14 }}
                  />
                )
              }
            />
            <Box className={classes.textCol}>
              <Typography className={classes.date}>
                {formatBlockDate(block.startTime, block.endTime, block.allDay)}
              </Typography>
              <Typography variant="body2" className={classes.meta} noWrap>
                {[timeText, note].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Box className={classes.actions}>
              {isSynced ? (
                <Tooltip
                  title="Synced from your connected Google Calendar"
                  placement="left"
                >
                  <Box className={classes.syncedIndicator}>
                    <SyncAltIcon fontSize="small" />
                  </Box>
                </Tooltip>
              ) : (
                <BlockTimeDestroyDialog
                  blockTime={block}
                  onSuccess={onChanged}
                  trigger={
                    <IconButton size="small" aria-label="delete blocked time">
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  }
                />
              )}
            </Box>
          </Card>
        )
      })}
    </Box>
  )
}

export default BlocksList
